import React, { useEffect } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useAuditHistory } from '../hooks/useAuditHistory';
import AuditHistoryHeader from './AuditHistoryHeader';
import { auditHistoryStyles as styles } from '../styles/auditHistory.styles';
import { formatAuditHistoryDate, getActionDescriptionParts } from '../utils/auditHistoryUtils';
import { useTheme } from '../../../theme';

interface AuditHistoryProps {
  actionPlanId: string | number;
  status?: string;
  assignmentStatus?: string;
  createdDate?: string;
  createdBy?: string;
  targetDate?: string;
  refreshKey?: number;
  isModal?: boolean; // When true, renders without inner ScrollView for mobile modal
  origin?: string; // When provided, displays origin information (e.g., "Stand-up")
  isTemp?: boolean | number; // When true or 1, adds is_temp parameter to API call
  onQuestionTitleLoaded?: (questionTitle: string | null) => void; // Callback to pass QUESTION_TITLE to parent
}

const AuditHistory: React.FC<AuditHistoryProps> = ({
  actionPlanId,
  status,
  assignmentStatus,
  createdDate,
  createdBy,
  targetDate,
  refreshKey,
  isModal = false,
  origin,
  isTemp,
  onQuestionTitleLoaded,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const { auditHistory, loading, error, refetch, questionTitle } = useAuditHistory({
    actionPlanId,
    refreshKey,
    isTemp,
  });

  // Auto-refresh when parent signals a change (e.g., after accept/decline/complete)
  // Include `status` so updates that change the plan status (e.g., Completed) trigger a refetch
  useEffect(() => {
    let mounted = true;
    const doRefetch = async () => {
      if (typeof refetch !== 'function') return;
      try {
        // debug: log what triggered the refetch
        // eslint-disable-next-line no-console
        console.log('[AuditHistory] refetch triggered', { actionPlanId, refreshKey, assignmentStatus, status });
        await refetch();
        if (mounted) {
          // eslint-disable-next-line no-console
          console.log('[AuditHistory] refetch completed');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AuditHistory] refetch error', err);
      }
    };

    doRefetch();

    return () => {
      mounted = false;
    };
  }, [refreshKey, assignmentStatus, actionPlanId, status, refetch]);

  // Notify parent when QUESTION_TITLE is loaded from history API
  useEffect(() => {
    console.log('[AuditHistory] useEffect for onQuestionTitleLoaded:', {
      hasCallback: !!onQuestionTitleLoaded,
      questionTitle,
    });
    
    if (onQuestionTitleLoaded && questionTitle !== null) {
      console.log('[AuditHistory] ✅ Calling onQuestionTitleLoaded with:', questionTitle);
      onQuestionTitleLoaded(questionTitle);
    } else if (!onQuestionTitleLoaded) {
      console.log('[AuditHistory] ⚠️ No onQuestionTitleLoaded callback provided');
    } else if (questionTitle === null) {
      console.log('[AuditHistory] ⚠️ questionTitle is null, not calling callback');
    }
  }, [questionTitle, onQuestionTitleLoaded]);

  // Check if the action plan has been declined by checking assignment_status field
  const isDeclined = assignmentStatus?.toLowerCase() === 'declined';
  
  // Determine the display status: if declined, show "Declined", otherwise use the passed status
  const displayStatus = isDeclined ? 'Declined' : status;

  if (loading) {
    return (
      <View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]}>
        <View style={[styles.header, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default }]}>
          <Text style={[styles.title, isDark && { color: colors.text.primary }]}>Action plan audit trail</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={isDark ? colors.brand.primary : "#034895"} />
          <Text style={[styles.loadingText, isDark && { color: colors.text.secondary }]}>Loading audit history...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]}>
        <View style={[styles.header, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default }]}>
          <Text style={[styles.title, isDark && { color: colors.text.primary }]}>Action plan audit trail</Text>
        </View>
        <View style={styles.errorContainer}>
          <MaterialIcons name="error-outline" size={24} color={isDark ? colors.status.error : "#D32F2F"} />
          <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>{error}</Text>
          <TouchableOpacity style={[styles.retryButton, isDark && { backgroundColor: colors.brand.primary }]} onPress={refetch}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (auditHistory.length === 0) {
    return (
      <View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]}>
        <View style={[styles.header, isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default }]}>
          <Text style={[styles.title, isDark && { color: colors.text.primary }]}>Action plan audit trail</Text>
        </View>
        <View style={styles.emptyContainer}>
          <MaterialIcons name="history" size={24} color={isDark ? colors.text.disabled : "#999"} />
          <Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>No audit history available</Text>
        </View>
      </View>
    );
  }

  // Render history items list
  const renderHistoryItems = () => (
    <>
      {auditHistory.map((item) => (
        <View key={item.ID} style={styles.logItem}>
          <View style={styles.iconContainer}>
            <View style={[styles.iconCircle, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              <MaterialIcons
                name="person"
                size={20}
                color={isDark ? colors.brand.primary : "#034895"}
              />
            </View>
          </View>

          <View style={styles.contentContainer}>
            <Text style={[styles.actionText, isDark && { color: colors.text.primary }]}>
              {getActionDescriptionParts(item).map((part, idx) => (
                <Text key={idx} style={part.bold ? { fontWeight: '700' } : {}}>
                  {part.text}
                </Text>
              ))}
            </Text>

            {item.NOTES && (
              <Text style={[styles.notesText, isDark && { color: colors.text.secondary }, { marginTop: 5 }]}>{item.NOTES}</Text>
            )}

            <Text style={[styles.timestampText, isDark && { color: colors.text.secondary }, { marginTop: item.NOTES ? 6 : 6 }]}>
              {formatAuditHistoryDate(item.TIMESTAMP)}
            </Text>
          </View>
        </View>
      ))}
    </>
  );

  return (
    <View style={isModal ? undefined : [styles.outerContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
      <AuditHistoryHeader
        status={displayStatus}
        createdDate={createdDate}
        createdBy={createdBy}
        targetDate={targetDate}
        origin={origin}
      />

      <View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]}>
        <View style={[styles.divider, isDark && { backgroundColor: colors.border.default }]} />

        {isModal ? (
          // For mobile modal - no inner ScrollView, parent handles scrolling
          <View style={[styles.activityContainer, { maxHeight: undefined }]}>
            {renderHistoryItems()}
          </View>
        ) : (
          // For web/tablet - use ScrollView with maxHeight
          <ScrollView
            style={styles.activityContainer}
            showsVerticalScrollIndicator={true}
            persistentScrollbar={true}
          >
            {renderHistoryItems()}
          </ScrollView>
        )}
      </View>
    </View>
  );
};

export default AuditHistory;

